
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Avatar } from "@/components/ui/Avatar";
import { VerifiedBadge } from "@/components/ui/VerifiedBadge";
import { VoteButtons } from "@/components/post/VoteButtons";
import { PostMediaPreview } from "@/components/post/PostMediaPreview";
import { PostMeta } from "@/components/post/PostMeta";
import { PostActions } from "@/components/post/PostActions";
import { TopicPill } from "@/components/post/TopicPill";
import { cn } from "@/lib/utils/cn";
import { formatTimeAgo, formatCount, buildPostUrl, buildProfileUrl } from "@/lib/utils/format";
import type { Post } from "@/types";

interface PostCardProps {
  post: Post;
  userId: string | null;
  variant?: "feed" | "compact" | "featured";
  showTopic?: boolean;
}

export function PostCard({
  post,
  userId,
  variant = "feed",
  showTopic = true,
}: PostCardProps) {
  const [localVote, setLocalVote] = useState(post.user_vote);
  const [localScore, setLocalScore] = useState(post.vote_score);
  const [localBookmarked, setLocalBookmarked] = useState(post.user_bookmarked);

  const handleVoteChange = useCallback(
    (newVote: "up" | "down" | null, scoreDelta: number) => {
      setLocalVote(newVote);
      setLocalScore((s) => s + scoreDelta);
    },
    []
  );

  const postUrl = buildPostUrl(post.slug, post.id);

  return (
    <article
      className={cn(
        "card group",
        variant === "compact" && "rounded-xl",
        "hover:shadow-card-hover transition-shadow duration-200"
      )}
      data-post-id={post.id}
    >
      <div className={cn(
        "flex gap-3",
        variant === "compact" ? "p-3" : "p-4"
      )}>
        {/* Vote buttons - side column */}
        <div className="flex flex-col items-center gap-0.5 flex-shrink-0 pt-0.5">
          <VoteButtons
            postId={post.id}
            userId={userId}
            currentVote={localVote}
            score={localScore}
            onVoteChange={handleVoteChange}
            size={variant === "compact" ? "sm" : "md"}
          />
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          {/* Header: topic + author + time */}
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            {showTopic && (
              <>
                <TopicPill topic={post.topic} size="xs" />
                <span className="text-text-subtle text-2xs">·</span>
              </>
            )}
            <Link to={buildProfileUrl(post.author.username)}
              className="flex items-center gap-1 group/author hover:text-foreground transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <Avatar
                src={post.author.avatar_url}
                alt={post.author.display_name}
                size="xs"
              />
              <span className="text-xs text-text-muted group-hover/author:text-foreground transition-colors">
                {post.author.display_name}
              </span>
              {post.author.is_verified && (
                <VerifiedBadge size="xs" />
              )}
            </Link>
            <span className="text-text-subtle text-2xs">·</span>
            <time
              className="text-xs text-text-subtle"
              dateTime={post.published_at}
              title={formatTimeAgo(post.published_at)}
            >
              {formatTimeAgo(post.published_at)}
            </time>

            {post.is_oc && (
              <span className="badge-brand text-2xs ml-1">OC</span>
            )}
            {post.is_locked && (
              <span className="badge-muted text-2xs ml-1">Locked</span>
            )}
          </div>

          {/* Title */}
          <Link to={postUrl} className="block group/title">
            <h2
              className={cn(
                "font-semibold text-foreground leading-snug mb-2",
                "group-hover/title:text-brand transition-colors duration-150",
                variant === "compact" ? "text-sm" : "text-base"
              )}
            >
              {post.is_nsfw && (
                <span className="badge-destructive text-2xs mr-1.5 align-middle">NSFW</span>
              )}
              {post.is_spoiler ? (
                <span className="blur-sm hover:blur-none transition-all duration-200">
                  {post.title}
                </span>
              ) : (
                post.title
              )}
            </h2>
          </Link>

          {/* Content preview */}
          {post.content && variant !== "compact" && (
            <Link to={postUrl} className="block">
              <p className="text-sm text-text-muted line-clamp-3 leading-relaxed mb-3">
                {post.content}
              </p>
            </Link>
          )}

          {/* Link preview */}
          {post.type === "link" && post.link_preview && variant !== "compact" && (
            <a
              href={post.link_url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="block mb-3"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex gap-3 rounded-xl border border-border bg-surface-overlay p-3 hover:bg-muted transition-colors group/link">
                {post.link_preview.image && (
                  <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
                    <img
                      src={post.link_preview.image}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground line-clamp-2 group-hover/link:text-brand transition-colors">
                    {post.link_preview.title}
                  </p>
                  <p className="text-xs text-text-subtle mt-0.5 flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    {post.link_preview.domain}
                  </p>
                </div>
              </div>
            </a>
          )}

          {/* Media preview */}
          {post.media && post.media.length > 0 && variant !== "compact" && (
            <div className="mb-3">
              <PostMediaPreview media={post.media} postUrl={postUrl} />
            </div>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && variant !== "compact" && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {post.tags.slice(0, 4).map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}&type=post`}
                  className="text-2xs px-2 py-0.5 rounded-full bg-muted text-text-muted hover:bg-brand-muted hover:text-brand transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  #{tag}
                </Link>
              ))}
            </div>
          )}

          {/* Actions row */}
          <PostActions
            post={post}
            userId={userId}
            localBookmarked={localBookmarked}
            onBookmarkChange={setLocalBookmarked}
            postUrl={postUrl}
          />
        </div>
      </div>
    </article>
  );
}
